import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API_URL } from '../config';

const API = API_URL;

const ProfileEditPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [name, setName] = useState('');
  const [profilePicture, setProfilePicture] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    setName(userData.name || '');
    setProfilePicture(userData.profile_picture || '');
    setPreviewUrl(userData.profile_picture || '');
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
      setProfilePicture(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: name.trim(),
          profile_picture: profilePicture
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update localStorage
        const updatedUser = { ...user, ...data.user };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        toast.success('Profile updated successfully');
        navigate('/profile');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A' }}>
      {/* Header */}
      <div 
        className="sticky top-0 z-50 border-b"
        style={{ background: '#121212', borderColor: '#2A2A2A' }}
      >
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-white text-xl font-bold">Edit Profile</h1>
          </div>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-[#EE1C4E] hover:bg-[#EE1C4E]/90 text-white"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="space-y-6">
          {/* Profile Picture */}
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <div 
                className="w-32 h-32 rounded-full flex items-center justify-center overflow-hidden"
                style={{ background: '#2A2A2A' }}
              >
                {previewUrl ? (
                  <img src={previewUrl} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white text-4xl font-bold">
                    {name ? name[0].toUpperCase() : user?.name?.[0]?.toUpperCase() || '?'}
                  </span>
                )}
              </div>
              <label
                htmlFor="profile-image"
                className="absolute bottom-0 right-0 p-2 rounded-full cursor-pointer transition-all hover:brightness-110"
                style={{ background: '#EE1C4E' }}
              >
                <Camera className="w-5 h-5 text-white" />
                <input
                  id="profile-image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            </div>
            <p className="text-gray-400 text-sm">Click the camera icon to upload a photo</p>
          </div>

          {/* Name Input */}
          <div className="space-y-2">
            <label className="text-white text-sm font-semibold">Display Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              className="w-full px-4 py-3 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#EE1C4E]"
              style={{ background: '#1A1A1A', border: '1px solid #2A2A2A' }}
            />
          </div>

          {/* Account Info (Read-only) */}
          <div className="space-y-2">
            <label className="text-white text-sm font-semibold">Email</label>
            <div 
              className="w-full px-4 py-3 rounded-lg text-gray-400"
              style={{ background: '#1A1A1A', border: '1px solid #2A2A2A' }}
            >
              {user?.email}
            </div>
            <p className="text-gray-500 text-xs">Email cannot be changed</p>
          </div>

          <div className="space-y-2">
            <label className="text-white text-sm font-semibold">Account Type</label>
            <div 
              className="w-full px-4 py-3 rounded-lg text-gray-400 flex items-center gap-2"
              style={{ background: '#1A1A1A', border: '1px solid #2A2A2A' }}
            >
              {user?.is_premium ? '👑 Premium' : 'Free'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditPage;
